import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import PageCTA from '../components/sections/pageCTA.jsx';


// ─── ICON ──────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  chaos: "M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0 0v-8m0 0L8 9m4 5l4-5",
  heka: "M13 10V3L4 14h7v7l9-11h-7z",
  order: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z",
  target: "M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 100 12 6 6 0 000-12z M12 10a2 2 0 100 4 2 2 0 000-4z",
  compass: "M12 2L2 7l10 5 10-5-10-5z M12 22v-10 M7 9l5 3 5-3",
  layers: "M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5",
  spark: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  orbit: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  arrow: "M5 12h14M12 5l7 7-7 7",
};

// ─── STYLES ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .vp-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: vp-shimmer 8s linear infinite;
  }
  @keyframes vp-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .vp-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  .vp-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251,191,36,0.3), transparent);
  }

  @keyframes vp-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .vp-fade   { animation: vp-fadeUp 0.7s ease forwards; }
  .vp-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .vp-fade-2 { animation-delay: 0.15s; opacity: 0; }
  .vp-fade-3 { animation-delay: 0.25s; opacity: 0; }
  .vp-fade-4 { animation-delay: 0.35s; opacity: 0; }
  .vp-fade-5 { animation-delay: 0.45s; opacity: 0; }

  /* ── Transformation Cycle (matching Intelligence Corner exactly) ── */
  .vp-node { transition: transform 0.3s ease, box-shadow 0.3s ease; }
  .vp-node:hover { transform: translateY(-3px); }

  @keyframes vp-flow {
    0%   { stroke-dashoffset: 24; opacity: 0.3; }
    50%  { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0.3; }
  }
  .vp-arrow-anim       { stroke-dasharray: 8 4; animation: vp-flow 1.8s ease-in-out infinite; }
  .vp-arrow-anim-delay { stroke-dasharray: 8 4; animation: vp-flow 1.8s ease-in-out 0.9s infinite; }

  @keyframes vp-heka-ring {
    0%   { transform: scale(1);    opacity: 0.35; }
    70%  { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }
  .vp-heka-ring { animation: vp-heka-ring 2.4s ease-out infinite; }

  /* ── Philosophy Principles ── */
  .vp-principle-card {
    transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .vp-principle-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.3);
  }

  @keyframes vp-icon-float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50%      { transform: translateY(-8px) rotate(5deg); }
  }
  .vp-icon-float {
    animation: vp-icon-float 3s ease-in-out infinite;
  }

  /* ── Philosophy Accents ── */
  @keyframes vp-scan {
    0%, 100% { transform: translateY(-20px); opacity: 0.3; }
    50%      { transform: translateY(20px); opacity: 1; }
  }
  .vp-scan-line {
    animation: vp-scan 2s ease-in-out infinite;
  }

  @keyframes vp-target-pulse {
    0%   { transform: scale(1); opacity: 0.5; }
    100% { transform: scale(1.3); opacity: 0; }
  }
  .vp-target-ring {
    animation: vp-target-pulse 2s ease-out infinite;
  }

  @keyframes vp-data-wave {
    0%, 100% { transform: scaleY(1); }
    50%       { transform: scaleY(0.4); }
  }
  .vp-data-bar {
    animation: vp-data-wave 1.2s ease-in-out infinite;
  }

  @keyframes vp-neural-glow {
    0%, 100% { opacity: 0.4; r: 4; }
    50%      { opacity: 1; r: 5; }
  }
  .vp-neural-node {
    animation: vp-neural-glow 2s ease-in-out infinite;
  }

  @keyframes vp-neural-fade {
    0%, 100% { opacity: 0.2; }
    50%      { opacity: 0.5; }
  }
  .vp-neural-connections {
    animation: vp-neural-fade 3s ease-in-out infinite;
  }

  @keyframes vp-orbit-spin {
    from { transform: rotate(0deg) translateX(32px) rotate(0deg); }
    to   { transform: rotate(360deg) translateX(32px) rotate(-360deg); }
  }
  .vp-orbit-dot {
    animation: vp-orbit-spin 4s linear infinite;
  }

  @keyframes vp-sparkle {
    0%, 100% { opacity: 0.3; transform: scale(0.8); }
    50%      { opacity: 1; transform: scale(1.2); }
  }
  .vp-sparkle-1 { animation: vp-sparkle 2s ease-in-out 0s infinite; }
  .vp-sparkle-2 { animation: vp-sparkle 2s ease-in-out 0.4s infinite; }
  .vp-sparkle-3 { animation: vp-sparkle 2s ease-in-out 0.8s infinite; }

  /* ── Pillar Cards ── */
  .vp-pillar {
    transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease;
    cursor: default;
  }
  .vp-pillar:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.3);
  }

  /* ── Manifesto Section ── */
  .vp-manifesto-item {
    transition: all 0.3s ease;
  }
  .vp-manifesto-item:hover {
    transform: translateX(8px);
  }
  .vp-manifesto-item:hover .vp-manifesto-bullet {
    background: linear-gradient(135deg, #f59e0b, #a78bfa);
    box-shadow: 0 0 20px rgba(251,191,36,0.5);
  }

  .vp-manifesto-bullet {
    transition: all 0.3s ease;
  }

  /* ── Journey Timeline ── */
  @keyframes vp-timeline-draw {
    from { height: 0%; }
    to   { height: 100%; }
  }
  .vp-timeline-line {
    animation: vp-timeline-draw 1.5s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both;
  }

  .vp-timeline-card {
    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .vp-timeline-card:hover {
    transform: translateX(-6px);
    border-color: rgba(251,191,36,0.4);
  }

  /* ── CTA Buttons ── */
  .vp-btn-primary {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .vp-btn-primary::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .vp-btn-primary:hover::after { transform: translateX(100%); }
  .vp-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }

  .vp-btn-secondary { transition: all 0.3s ease; }
  .vp-btn-secondary:hover {
    transform: translateY(-2px);
    background: rgba(109,40,217,0.1);
    border-color: rgba(109,40,217,0.6);
    color: #c4b5fd;
  }
`;

// ─── DATA ─────────────────────────────────────────────────────────────────────
const CORE_PILLARS = [
  {
    icon: ICONS.layers,
    title: "Systems Thinking",
    desc: "We model the whole — not just the immediate problem. Feedback loops, emergent behaviors, and second-order effects are native to our analytical framework. At HEKAOS, understanding a system means understanding what it becomes under pressure, not just what it does by design.",
    color: "#f59e0b",
  },
  {
    icon: ICONS.spark,
    title: "Engineered Insight",
    desc: "Every recommendation is traceable. Every decision carries a clear line of reasoning. We reject opacity as a design choice — intelligence you cannot interrogate is intelligence you cannot trust. We build AI you can question, verify, and hold accountable.",
    color: "#8b5cf6",
  },
  {
    icon: ICONS.orbit,
    title: "Mythic Architecture",
    desc: "HEKAOS draws from Heka — the ancient Egyptian force through which structure emerges from chaos. We believe the oldest frameworks for understanding intelligence still hold. Our platform is built on that conviction: that timeless principles, not passing trends, are what make intelligence durable.",
    color: "#ec4899",
  },

];

const MANIFESTO_POINTS = [
  "These are the beliefs that drive every product decision, every design choice, every line of code we write.",
  "Intelligence is structured understanding that survives contact with reality.",
  "Chaos is unstructured potential. We engineer the signal from the noise.",
  "Human judgment is the objective function. We sharpen it — never circumvent it.",
  "Every insight must be explainable, traceable, and contestable.",
  "Systems that adapt outlast systems that merely optimize.",
  "At HEKAOS, how we think and what we build are the same act.",
];

const JOURNEY_MILESTONES = [
  {
    year: "2024",
    title: "Foundation",
    desc: "An idea takes root. We begin asking harder questions about how intelligence systems should be built — and why most of them fall short.",
  },
  {
    year: "2025",
    title: "Emergence",
    desc: "Concepts become architecture. The HEKAOS platform begins to take shape, moving from philosophy into tangible systems and early builds.",
  },
  {
    year: "2026",
    title: "Expansion",
    desc: "From internal development to real-world application. We begin deploying across industries, learning from every engagement, and building a community around a shared belief in better decision intelligence.",
    active: true
  },
  {
    year: "Next",
    title: "Foresight Engine",
    desc: "A unified layer where data, models, and human intent work together in real time. The destination we've been building toward from day one.",
    active: true,
  },
];

// ─── TRANSFORMATION CYCLE (MATCHING INTELLIGENCE CORNER EXACTLY) ──────────────
const TransformationCycle = () => (
  <section className="vp-fade vp-fade-2">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        Core Process
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
        The HEKAOS Transformation
      </h2>
      <p className="text-slate-400 mt-3 max-w-xl mx-auto text-base leading-relaxed">
        At the heart of our methodology is a single, powerful process: taking unstructured complexity
        and converting it into structured, actionable intelligence. We call this the HEKAOS Transformation.
        <br /><br />
        It begins with chaos—raw data, ambiguity, noise. Through HEKA, our intelligence engine, we impose
        structure on entropy. What emerges is clarity: decision-ready intelligence that organizations can
        act on with confidence.
        <br /><br />
        Chaos → Transform → Order. Every engagement follows this arc.
      </p>
    </div>

    {/* Desktop Layout - exact match to Intelligence Corner */}
    <div className="hidden md:flex items-center justify-center gap-0 max-w-5xl mx-auto">
      {/* CHAOS */}
      <div className="vp-node flex flex-col items-center gap-4 w-56">
        <div className="w-24 h-24 rounded-2xl border border-red-800/40 bg-red-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(239,68,68,0.12)" }}>
          <Icon d={ICONS.chaos} size={38} className="text-red-400" />
        </div>
        <div className="text-center">
          <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1.5">CHAOS</p>
          <p className="text-slate-500 text-sm leading-relaxed">
            Unstructured complexity.<br />Ambiguity and noise.
          </p>
        </div>
      </div>

      {/* Arrow 1 */}
      <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
        <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
          <line x1="0" y1="12" x2="96" y2="12" stroke="#ef4444" strokeWidth="2" className="vp-arrow-anim" />
          <polyline points="86,5 100,12 86,19" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">TRANSFORM</span>
      </div>

      {/* HEKA */}
      <div className="vp-node flex flex-col items-center gap-4 relative">
        <div className="absolute w-36 h-36 rounded-full border border-violet-500/15 vp-heka-ring" />
        <div className="w-36 h-36 rounded-2xl border border-violet-600/50 flex items-center justify-center relative z-10"
          style={{
            background: "linear-gradient(135deg, rgba(109,40,217,0.28), rgba(139,92,246,0.16))",
            boxShadow: "0 0 48px rgba(139,92,246,0.22), inset 0 1px 0 rgba(167,139,250,0.15)",
          }}>
          <Icon d={ICONS.heka} size={52} className="text-violet-400" />
        </div>
        <div className="text-center mt-1">
          <p className="text-violet-400 font-bold text-base tracking-widest uppercase mb-1.5">HEKA</p>
          <p className="text-slate-500 text-sm leading-relaxed max-w-[11rem]">
            Intelligence engine.<br />Structure from entropy.
          </p>
        </div>
      </div>

      {/* Arrow 2 */}
      <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
        <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
          <line x1="0" y1="12" x2="96" y2="12" stroke="#22d3ee" strokeWidth="2" className="vp-arrow-anim-delay" />
          <polyline points="86,5 100,12 86,19" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">SYNTHESIZE</span>
      </div>

      {/* ORDER */}
      <div className="vp-node flex flex-col items-center gap-4 w-56">
        <div className="w-24 h-24 rounded-2xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center"
          style={{ boxShadow: "0 0 28px rgba(34,211,238,0.12)" }}>
          <Icon d={ICONS.order} size={38} className="text-cyan-400" />
        </div>
        <div className="text-center">
          <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1.5">ORDER</p>
          <p className="text-slate-500 text-sm leading-relaxed">
            Actionable intelligence.<br />Clarity for decisions.
          </p>
        </div>
      </div>
    </div>

    {/* Mobile Layout - exact match to Intelligence Corner */}
    <div className="md:hidden flex flex-col items-center gap-0 max-w-sm mx-auto">
      <div className="vp-node flex items-center gap-5 w-full rounded-xl border border-red-800/30 bg-slate-900 px-6 py-5">
        <div className="w-16 h-16 rounded-xl border border-red-800/40 bg-red-900/20 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.chaos} size={28} className="text-red-400" />
        </div>
        <div>
          <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1">CHAOS</p>
          <p className="text-slate-500 text-sm leading-relaxed">Unstructured complexity and noise</p>
        </div>
      </div>

      <div className="flex flex-col items-center py-3">
        <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
          <line x1="12" y1="0" x2="12" y2="34" stroke="#a78bfa" strokeWidth="2" strokeDasharray="6 3"
            style={{ animation: "vp-flow 1.8s ease-in-out infinite" }} />
          <polyline points="5,26 12,38 19,26" stroke="#a78bfa" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="vp-node flex items-center gap-5 w-full rounded-xl px-6 py-5"
        style={{ border: "1px solid rgba(139,92,246,0.4)", background: "linear-gradient(135deg, rgba(109,40,217,0.2), rgba(139,92,246,0.1))", boxShadow: "0 0 32px rgba(139,92,246,0.18)" }}>
        <div className="w-16 h-16 rounded-xl border border-violet-600/40 bg-violet-900/30 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.heka} size={30} className="text-violet-400" />
        </div>
        <div>
          <p className="text-violet-400 font-bold text-sm tracking-widest uppercase mb-1">HEKA</p>
          <p className="text-slate-400 text-sm leading-relaxed">Intelligence engine transforms data</p>
        </div>
      </div>

      <div className="flex flex-col items-center py-3">
        <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
          <line x1="12" y1="0" x2="12" y2="34" stroke="#22d3ee" strokeWidth="2" strokeDasharray="6 3"
            style={{ animation: "vp-flow 1.8s ease-in-out 0.9s infinite" }} />
          <polyline points="5,26 12,38 19,26" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <div className="vp-node flex items-center gap-5 w-full rounded-xl border border-cyan-800/30 bg-slate-900 px-6 py-5">
        <div className="w-16 h-16 rounded-xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center flex-shrink-0">
          <Icon d={ICONS.order} size={28} className="text-cyan-400" />
        </div>
        <div>
          <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1">ORDER</p>
          <p className="text-slate-500 text-sm leading-relaxed">Structured insight. Ready for execution.</p>
        </div>
      </div>
    </div>
  </section>
);

// ─── PHILOSOPHY PRINCIPLES ────────────────────────────────────────────────────
// ─── OUTCOME-BASED STRATEGY ACCENTS ──────────────────────────────────────────

// Objective First - Goal hierarchy: goal node at top, architecture flows below
const ObjectiveAccent = () => (
  <div className="relative h-20 flex items-center justify-center overflow-hidden">
    <svg width="120" height="80" viewBox="0 0 120 80" fill="none">
      {/* Lines from goal down to architecture nodes */}
      <line x1="60" y1="18" x2="30" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="60" y1="18" x2="60" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      <line x1="60" y1="18" x2="90" y2="55" stroke="rgba(245,158,11,0.25)" strokeWidth="1.5" strokeDasharray="3 3" />
      {/* Goal node — top, brighter */}
      <circle cx="60" cy="14" r="6" fill="rgba(245,158,11,0.7)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="60" cy="14" r="10" fill="none" stroke="rgba(245,158,11,0.2)" strokeWidth="1" />
      {/* Architecture nodes — bottom row, dimmer */}
      <rect x="22" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      <rect x="52" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      <rect x="82" y="50" width="16" height="10" rx="3" fill="rgba(245,158,11,0.2)" stroke="rgba(245,158,11,0.3)" strokeWidth="1" />
      {/* Label */}
      <text x="60" y="76" textAnchor="middle" fontSize="7" fill="rgba(245,158,11,0.35)" fontFamily="monospace" letterSpacing="2">GOAL · ARCH</text>
    </svg>
  </div>
);

// Output Over Process - Signal pulse: faint process line, bold output spike
const OutputAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="120" height="56" viewBox="0 0 120 56" fill="none">
      {/* Baseline process line — faint, flat */}
      <polyline
        points="10,36 30,34 50,37 70,35 90,36 110,35"
        stroke="rgba(236,72,153,0.2)" strokeWidth="1.5" strokeLinejoin="round" fill="none"
      />
      {/* Output spike — bright, sharp */}
      <polyline
        points="10,36 40,36 55,10 70,36 110,36"
        stroke="rgba(236,72,153,0.7)" strokeWidth="2" strokeLinejoin="round" fill="none"
        className="vp-scan-line"
      />
      {/* Output peak dot */}
      <circle cx="55" cy="10" r="3.5" fill="rgba(236,72,153,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="55" cy="10" r="7" fill="none" stroke="rgba(236,72,153,0.2)" strokeWidth="1" />
      {/* Labels */}
      <text x="18" y="50" fontSize="6.5" fill="rgba(236,72,153,0.25)" fontFamily="monospace" letterSpacing="1.5">PROCESS</text>
      <text x="72" y="18" fontSize="6.5" fill="rgba(236,72,153,0.5)" fontFamily="monospace" letterSpacing="1.5">OUTPUT</text>
    </svg>
  </div>
);

// Iterative Convergence - Spiral loops tightening toward a center point
const ConvergenceAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="120" height="56" viewBox="0 0 120 56" fill="none">
      {/* Outer loop — faint */}
      <ellipse cx="60" cy="28" rx="48" ry="20" stroke="rgba(139,92,246,0.15)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Mid loop */}
      <ellipse cx="60" cy="28" rx="32" ry="13" stroke="rgba(139,92,246,0.3)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Inner loop — brightest */}
      <ellipse cx="60" cy="28" rx="16" ry="7" stroke="rgba(139,92,246,0.55)" strokeWidth="1.5" strokeDasharray="4 3" />
      {/* Convergence point */}
      <circle cx="60" cy="28" r="3.5" fill="rgba(139,92,246,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="60" cy="28" r="6" fill="none" stroke="rgba(139,92,246,0.3)" strokeWidth="1" />
      {/* Iteration arrows hint */}
      <text x="60" y="52" textAnchor="middle" fontSize="8" fill="rgba(167,139,250,0.7)" fontFamily="monospace" letterSpacing="1">ITERATE · CONVERGE</text>

    </svg>
  </div>
);

// Adaptive Calibration - Waveform that shifts and re-aligns mid-chart
// Adaptive Calibration — Gauge dial with needle locking onto target zone
const CalibrationAccent = () => (
  <div className="relative h-16 flex items-center justify-center overflow-hidden">
    <svg width="100" height="56" viewBox="0 0 100 56" fill="none">
      
      {/* Gauge arc — full range */}
      <path d="M12,50 A38,38 0 0,1 88,50" stroke="rgba(6,182,212,0.15)" strokeWidth="4" strokeLinecap="round" fill="none" />
      
      {/* Danger zone — left, red-ish */}
      <path d="M12,50 A38,38 0 0,1 33,18" stroke="rgba(6,182,212,0.25)" strokeWidth="4" strokeLinecap="round" fill="none" />
      
      {/* Calibrated zone — right of center, bright cyan */}
      <path d="M50,12 A38,38 0 0,1 88,50" stroke="rgba(6,182,212,0.75)" strokeWidth="4" strokeLinecap="round" fill="none" />

      {/* Tick marks */}
      {[0, 30, 60, 90, 120, 150, 180].map((angle, i) => {
        const rad = ((angle - 180) * Math.PI) / 180;
        const x1 = 50 + 34 * Math.cos(rad);
        const y1 = 50 + 34 * Math.sin(rad);
        const x2 = 50 + 28 * Math.cos(rad);
        const y2 = 50 + 28 * Math.sin(rad);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={`rgba(6,182,212,${i > 3 ? 0.6 : 0.2})`} strokeWidth="1.5" strokeLinecap="round" />;
      })}

      {/* Needle — pointing to calibrated zone */}
      <line x1="50" y1="50" x2="74" y2="20"
        stroke="rgba(6,182,212,0.9)" strokeWidth="2" strokeLinecap="round" />

      {/* Needle pivot */}
      <circle cx="50" cy="50" r="4" fill="rgba(6,182,212,1)" />
      <circle cx="50" cy="50" r="7" fill="none" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />

      {/* Lock dot on arc — where needle points */}
      <circle cx="76" cy="18" r="3" fill="rgba(6,182,212,0.9)" className="vp-neural-node" style={{ animationDelay: '0s' }} />

    </svg>
  </div>
);
// Pattern Recognition accent - Scanning lines
const PatternAccent = () => (
  <div className="relative h-20 flex items-center justify-center overflow-hidden">
    <svg width="100" height="80" viewBox="0 0 100 80" fill="none">
      <defs>
        <pattern id="grid-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <circle cx="10" cy="10" r="1" fill="rgba(245,158,11,0.3)" />
        </pattern>
      </defs>
      <rect width="100" height="80" fill="url(#grid-pattern)" />
      <line x1="0" y1="40" x2="100" y2="40" stroke="rgba(245,158,11,0.6)" strokeWidth="2" className="vp-scan-line" />
    </svg>
  </div>
);

// Intent Alignment accent - Target circles
const TargetAccent = () => (
  <div className="relative h-20 flex items-center justify-center">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="absolute rounded-full border vp-target-ring"
        style={{
          width: 20 + i * 16,
          height: 20 + i * 16,
          borderColor: `rgba(236,72,153,${0.5 - i * 0.1})`,
          borderWidth: 2,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <div className="absolute w-3 h-3 rounded-full bg-pink-400 shadow-[0_0_12px_rgba(236,72,153,0.8)]" />
  </div>
);

// Evidence-Based accent - Data bars flowing
const DataFlowAccent = () => (
  <div className="flex items-center gap-1.5 h-16">
    {[0.5, 0.8, 0.6, 1, 0.7, 0.9, 0.55, 0.85].map((h, i) => (
      <div key={i}
        className="rounded vp-data-bar"
        style={{
          width: 6,
          height: 56 * h,
          background: `linear-gradient(to top, rgba(139,92,246,${0.4 + h * 0.4}), rgba(139,92,246,${0.2 + h * 0.2}))`,
          transformOrigin: "bottom",
          animationDelay: `${i * 0.1}s`,
        }}
      />
    ))}
  </div>
);

// Adaptive Intelligence accent - Neural network nodes
const NeuralAccent = () => (
  <div className="relative h-20 flex items-center justify-center">
    <svg width="120" height="80" viewBox="0 0 120 80">
      <g className="vp-neural-connections">
        <line x1="30" y1="20" x2="60" y2="40" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="30" y1="60" x2="60" y2="40" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="60" y1="40" x2="90" y2="20" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
        <line x1="60" y1="40" x2="90" y2="60" stroke="rgba(6,182,212,0.3)" strokeWidth="1.5" />
      </g>
      <circle cx="30" cy="20" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0s' }} />
      <circle cx="30" cy="60" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.2s' }} />
      <circle cx="60" cy="40" r="5" fill="rgba(6,182,212,0.6)" className="vp-neural-node" style={{ animationDelay: '0.4s' }} />
      <circle cx="90" cy="20" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.6s' }} />
      <circle cx="90" cy="60" r="4" fill="rgba(6,182,212,0.4)" className="vp-neural-node" style={{ animationDelay: '0.8s' }} />
    </svg>
  </div>
);

const PhilosophySection = () => (
  <section className="vp-fade vp-fade-3">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        Our Philosophy
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight mb-4">
        Outcome-Based Strategy
      </h2>
      <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed text-base">
        Every system has an objective function. <br />Ours is real-world impact — defined, tracked, and refined until the result is undeniable.

      </p>
    </div>

    {/* Bento grid: 1 large + 3 smaller */}
    <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:h-[480px]">

      {/* Objective First - Large card spanning 2 rows */}
      <div className="vp-principle-card md:row-span-2 rounded-2xl border border-amber-800/30 
        hover:border-amber-500/40 bg-slate-900 p-7 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 20% 80%, rgba(251,191,36,0.06) 0%, transparent 60%)" }} />

        <div>
          <div className="w-12 h-12 rounded-xl bg-amber-900/30 border border-amber-800/40 flex items-center justify-center mb-5">
            <Icon d={ICONS.eye} size={22} className="text-amber-400" />
          </div>
          <h3 className="text-amber-400 font-bold text-lg tracking-tight mb-2">Objective First </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            The architecture follows the goal — never the other way around. We define success before we design the system that achieves it. Most systems fail not because of poor execution, but because the objective was never precisely defined. At HEKAOS, clarity of intent is the first engineering problem we solve — everything else is downstream of that.
          </p>
        </div>
        <div className="mt-6 relative h-20 flex items-end">
          <ObjectiveAccent />
          <p className="absolute bottom-0 right-0 text-xs text-amber-400/40  tracking-wide uppercase">
            Intent · Blueprint · Execute
          </p>
        </div>
      </div>

      {/* Output Over Process */}
      {/* Output Over Process */}
      <div className="vp-principle-card rounded-2xl border border-pink-800/30 hover:border-pink-500/40 
  bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(236,72,153,0.06) 0%, transparent 60%)" }} />
        <div>
          {/* Icon + Accent in same row */}
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-pink-900/30 border border-pink-800/40 flex items-center justify-center">
              <Icon d={ICONS.target} size={20} className="text-pink-400" />
            </div>
            <OutputAccent />
          </div>

          <h3 className="text-pink-400 font-bold text-base tracking-tight mb-1.5">Output Over Process</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            What a system produces is the only metric that matters. We measure real-world results, not internal activity.
          </p>
        </div>
      </div>

      {/* Iterative Convergence */}
      <div className="vp-principle-card rounded-2xl border border-violet-800/30 hover:border-violet-500/40 
  bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(139,92,246,0.06) 0%, transparent 60%)" }} />
        <div>
          {/* Icon + Accent in same row */}
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 rounded-xl bg-violet-900/30 border border-violet-800/40 flex items-center justify-center">
              <Icon d={ICONS.compass} size={20} className="text-violet-400" />
            </div>
            <ConvergenceAccent />
          </div>

          <h3 className="text-violet-400 font-bold text-base tracking-tight mb-1.5">Iterative Convergence</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            We build, test, and close the gap between intended and actual outcomes — repeatedly, until the result holds
          </p>
        </div>
      </div>

      {/* Adaptive Calibration */}
      <div className="vp-principle-card md:col-span-2 rounded-2xl border border-cyan-800/25 hover:border-cyan-500/35 
        bg-slate-900 p-6 flex items-center gap-8 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 100% 50%, rgba(6,182,212,0.05) 0%, transparent 60%)" }} />
        <NeuralAccent />
        <div className="flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-cyan-900/25 border border-cyan-800/35 flex items-center justify-center mb-4">
            <Icon d={ICONS.spark} size={20} className="text-cyan-400" />
          </div>
          <h3 className="text-cyan-400 font-bold text-base tracking-tight mb-1.5">Adaptive Calibration</h3>
          <p className="text-slate-400 text-xs leading-relaxed">
            When the environment shifts, the objective function evolves with it. Static strategy is a liability in dynamic systems.
          </p>
        </div>
      </div>

    </div>


  </section>
);

// ─── CORE PILLARS ─────────────────────────────────────────────────────────────
const PillarsSection = () => (
  <section className="vp-fade vp-fade-3">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        Foundation
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
        Three Pillars of HEKAOS
      </h2>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {CORE_PILLARS.map((pillar, i) => (
        <div key={i} className="vp-pillar rounded-2xl border border-slate-700/40 bg-slate-900 p-8 
          hover:border-slate-600/50 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20"
            style={{ background: pillar.color }} />

          {/* Animated accent based on index */}
          <div className="relative mb-6 flex items-center justify-center h-20">
            {i === 0 && (
              <div className="relative">
                {[0, 1, 2].map((n) => (
                  <div key={n} className="absolute"
                    style={{
                      width: 40 + n * 12,
                      height: 8,
                      background: `rgba(251,191,36,${0.15 + n * 0.1})`,
                      border: `1px solid rgba(251,191,36,${0.2 + n * 0.1})`,
                      borderRadius: 4,
                      bottom: n * 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      animation: `vp-icon-float 3s ease-in-out ${n * 0.2}s infinite`,
                    }}
                  />
                ))}
              </div>
            )}
            {i === 1 && (
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="w-5 h-5 rounded-full bg-pink-400/40 border border-pink-400/60" />
                <div className="absolute w-16 h-16 rounded-full border border-dashed border-pink-400/20" />
                <div className="absolute vp-orbit-dot" style={{ width: 16, height: 16 }}>
                  <div className="w-2 h-2 rounded-full bg-pink-400 shadow-[0_0_8px_rgba(236,72,153,0.8)] vp-sparkle-1" />
                </div>
                <div className="absolute vp-orbit-dot" style={{ width: 16, height: 16, animationDelay: '2s' }}>
                  <div className="w-2 h-2 rounded-full bg-pink-400/60 vp-sparkle-2" />
                </div>
              </div>
            )}
            {i === 2 && (
              <div className="flex items-center gap-1">
                <div className="w-1 h-8 rounded-full bg-violet-400/30 vp-sparkle-1" />
                <div className="w-1 h-12 rounded-full bg-violet-400/50 vp-sparkle-2" />
                <div className="w-1 h-16 rounded-full bg-violet-400/70 vp-sparkle-3" />
                <div className="w-1 h-12 rounded-full bg-violet-400/50 vp-sparkle-1"
                  style={{ animationDelay: '0.2s' }} />
                <div className="w-1 h-8 rounded-full bg-violet-400/30 vp-sparkle-2"
                  style={{ animationDelay: '0.4s' }} />
              </div>
            )}
          </div>

          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
            style={{
              background: `${pillar.color}20`,
              border: `1px solid ${pillar.color}40`,
            }}>
            <Icon d={pillar.icon} size={24} style={{ color: pillar.color }} />
          </div>

          <h3 className="text-slate-100 font-bold text-xl mb-3 tracking-tight">{pillar.title}</h3>
          <p className="text-slate-400 text-sm leading-relaxed">{pillar.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

// ─── MANIFESTO ────────────────────────────────────────────────────────────────
const ManifestoSection = () => (
  <section className="vp-fade vp-fade-4">
    <div className="rounded-3xl border border-slate-700/50 bg-gradient-to-br from-slate-900 to-slate-800 p-10 md:p-14 max-w-4xl mx-auto">
      <div className="text-center mb-10">
        <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
          Our Beliefs
        </p>
        <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
          The HEKAOS Manifesto
        </h2>
      </div>

      <div className="space-y-5">
        {MANIFESTO_POINTS.map((point, i) => (
          <div key={i} className="vp-manifesto-item flex items-start gap-5 group">
            <div className="vp-manifesto-bullet w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #8b5cf6, #06b6d4)",
                boxShadow: "0 0 12px rgba(139,92,246,0.3)",
              }}
            />
            <p className="text-slate-300 leading-relaxed">{point}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── JOURNEY TIMELINE ─────────────────────────────────────────────────────────
const JourneySection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="vp-fade vp-fade-4">
      <div className="text-center mb-10">
        <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
          Our Journey
        </p>
        <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
          Evolution Timeline
        </h2>
      </div>

      <div className="relative max-w-3xl mx-auto">
        <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-slate-800" />
        {active && (
          <div className="absolute left-6 md:left-8 top-0 w-0.5 vp-timeline-line"
            style={{ background: "linear-gradient(to bottom, #f59e0b, #ec4899, #8b5cf6, #06b6d4)" }} />
        )}

        <div className="space-y-8">
          {JOURNEY_MILESTONES.map((m, i) => (
            <div key={i} className="relative pl-16 md:pl-20">
              <div className="absolute left-6 md:left-8 top-6 -translate-x-1/2 z-10">
                {m.active && (
                  <span className="absolute w-6 h-6 rounded-full animate-ping -top-1 -left-1"
                    style={{ background: "#06b6d4", opacity: 0.3 }} />
                )}
                <div className="w-4 h-4 rounded-full"
                  style={{
                    background: m.active ? "#06b6d4" : ["#f59e0b", "#ec4899", "#8b5cf6"][i] || "#06b6d4",
                    boxShadow: `0 0 0 3px #020617, 0 0 0 5px ${m.active ? "#06b6d444" : ["#f59e0b44", "#ec489944", "#8b5cf644"][i]}`,
                  }}
                />
              </div>

              <div className="vp-timeline-card rounded-xl border border-slate-700/50 bg-slate-900/70 p-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-sm font-bold tracking-widest uppercase"
                    style={{ color: m.active ? "#06b6d4" : ["#f59e0b", "#ec4899", "#8b5cf6"][i] }}>
                    {m.year}
                  </span>
                  <span className="text-slate-600">—</span>
                  <span className="text-lg font-bold text-slate-100">{m.title}</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{m.desc}</p>
                {m.active && (
                  <span className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-bold"
                    style={{
                      background: "rgba(6,182,212,0.15)",
                      color: "#06b6d4",
                      border: "1px solid rgba(6,182,212,0.3)",
                    }}>
                    In Progress
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const VisionPage = ({ setCurrentPage = () => {} }) => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash.replace('#', ''));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [hash]);

  return (   // ← note: now uses return() instead of just ()
    <>
    <style>{styles}</style>
    <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative pt-16 pb-10 text-center vp-fade vp-fade-1">
        <div className="vp-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />

        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/30 
          bg-amber-900/10 mb-6">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">Vision & Philosophy</span>
        </div>

        <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
          <span className="vp-title">Hekaos Axiom</span>
          {/* <br />
          <span className="text-slate-100">&amp; Philosophy</span> */}
        </h1>


        <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
          First principles for engineered intelligence—where chaos isn't erased,
          it's modeled, measured, and guided into order.
        </p>
      </section>

      <div className="vp-divider" />

      {/* ── TRANSFORMATION CYCLE ─────────────────────────── */}
      <TransformationCycle />

      <div className="vp-divider" id="jump-to-philosophy"/>

      {/* ── PHILOSOPHY PRINCIPLES ────────────────────────── */}
      <PhilosophySection />

      <div className="vp-divider" id="jump-to-pillars"/>

      {/* ── CORE PILLARS ─────────────────────────────────── */}
      <PillarsSection />

      <div className="vp-divider" id="jump-to-manifesto"/>

      {/* ── MANIFESTO ────────────────────────────────────── */}
      <ManifestoSection />

      <div className="vp-divider" />

      {/* ── JOURNEY TIMELINE ─────────────────────────────── */}
      <JourneySection />

      <div className="vp-divider" />

      {/* ── CTA ──────────────────────────────────────────── */}
      <PageCTA page="VisionPhilosophy" setCurrentPage={setCurrentPage} />
    </div>
  </>
);
};
export default VisionPage;